import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { formatDate, parseDateInput, toInputDateValue } from '../../utils/dateFormatter';
import './crm-ui.scss';


const normalizeDateText = (text, format) => {
  const value = String(text || '').trim();
  if (!value) return '';

  const parts = value.split(/[\/\-.]/).map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 3) return null;

  let day;
  let month;
  let year;
  if (format === 'MM/DD/YYYY') [month, day, year] = parts;
  else if (format === 'YYYY-MM-DD') [year, month, day] = parts;
  else [day, month, year] = parts;

  if (String(year).length === 2) year = `20${year}`;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) return null;

  return toInputDateValue(date);
};

const CrmField = ({
  id,
  label,
  required,
  error,
  helperText,
  className = '',
  children,
}) => (
  <div className={`crm-field ${error ? 'crm-field--error' : ''} ${className}`.trim()}>
    {label ? (
      <label className="crm-field__label" htmlFor={id}>
        {label}
        {required ? <span className="crm-field__required" aria-hidden="true"> *</span> : null}
      </label>
    ) : null}
    {children}
    {error || helperText ? (
      <span className="crm-field__message" role={error ? 'alert' : undefined}>
        {error || helperText}
      </span>
    ) : null}
  </div>
);

export const CrmInput = forwardRef(({
  id: providedId,
  label,
  error,
  helperText,
  className = '',
  inputClassName = '',
  required = false,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <CrmField id={id} label={label} required={required} error={error} helperText={helperText} className={className}>
      <input
        ref={ref}
        id={id}
        className={`crm-control ${inputClassName}`.trim()}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={error || helperText ? `${id}-message` : undefined}
        {...props}
      />
      {error || helperText ? <span id={`${id}-message`} hidden>{error || helperText}</span> : null}
    </CrmField>
  );
});

CrmInput.displayName = 'CrmInput';


export const CrmDateInput = ({
  id: providedId,
  label,
  value,
  onChange,
  dateFormat = 'DD/MM/YYYY',
  error,
  helperText,
  className = '',
  showFormatHint = true,
  required = false,
  disabled = false,
  ...props
}) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const pickerRef = useRef(null);
  const normalizedValue = toInputDateValue(value) || '';
  const [textValue, setTextValue] = useState(normalizedValue ? formatDate(normalizedValue, dateFormat) : '');

  useEffect(() => {
    setTextValue(normalizedValue ? formatDate(normalizedValue, dateFormat) : '');
  }, [normalizedValue, dateFormat]);

  const commitTextValue = () => {
    const normalized = normalizeDateText(textValue, dateFormat);
    if (normalized === '') {
      onChange?.('');
      return;
    }
    if (normalized) {
      setTextValue(formatDate(normalized, dateFormat));
      onChange?.(normalized);
    } else {
      setTextValue(normalizedValue ? formatDate(normalizedValue, dateFormat) : '');
    }
  };

  const openPicker = () => {
    if (disabled) return;
    const picker = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else picker.click();
  };

  return (
    <CrmField
      id={id}
      label={label}
      required={required}
      error={error}
      helperText={helperText || (showFormatHint && dateFormat ? `Format: ${dateFormat}` : undefined)}
      className={className}
    >
      <div className="crm-date-control">
        <input
          id={id}
          type="text"
          className="crm-control crm-control--date"
          value={textValue}
          placeholder={dateFormat}
          required={required}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          inputMode="numeric"
          onChange={(event) => setTextValue(event.target.value)}
          onClick={openPicker}
          onBlur={commitTextValue}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTextValue(); } }}
          {...props}
        />
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          className="crm-date-control__picker"
          value={normalizedValue}
          min={props.min}
          max={props.max}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value;
            setTextValue(next ? formatDate(next, dateFormat) : '');
            onChange?.(next);
          }}
        />
        <button type="button" className="crm-date-control__button" onClick={openPicker} disabled={disabled} aria-label={`Choose ${label || 'date'}`}>
          <CalendarMonthOutlinedIcon aria-hidden="true" />
        </button>
      </div>
    </CrmField>
  );
};


export const CrmTimeInput = forwardRef(({
  id: providedId,
  label,
  error,
  helperText,
  className = '',
  required = false,
  value,
  disabled = false,
  onClick,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  const openPicker = (event) => {
    onClick?.(event);
    if (disabled || event.defaultPrevented) return;
    const input = event.currentTarget;
    if (typeof input.showPicker === 'function') {
      try { input.showPicker(); } catch (_) {}
    }
  };

  return (
    <CrmField id={id} label={label} required={required} error={error} helperText={helperText} className={className}>
      <input
        ref={ref}
        id={id}
        type="time"
        className="crm-control crm-control--time"
        required={required}
        disabled={disabled}
        value={value == null ? '' : String(value).slice(0, 5)}
        aria-invalid={Boolean(error)}
        onClick={openPicker}
        {...props}
      />
    </CrmField>
  );
});

CrmTimeInput.displayName = 'CrmTimeInput';

export const CrmTextarea = forwardRef(({
  id: providedId,
  label,
  error,
  helperText,
  className = '',
  required = false,
  rows = 4,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <CrmField id={id} label={label} required={required} error={error} helperText={helperText} className={className}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className="crm-control crm-control--textarea"
        required={required}
        aria-invalid={Boolean(error)}
        {...props}
      />
    </CrmField>
  );
});

CrmTextarea.displayName = 'CrmTextarea';

export const CrmSelect = forwardRef(({
  id: providedId,
  label,
  error,
  helperText,
  className = '',
  required = false,
  children,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <CrmField id={id} label={label} required={required} error={error} helperText={helperText} className={className}>
      <select
        ref={ref}
        id={id}
        className="crm-control crm-control--select"
        required={required}
        aria-invalid={Boolean(error)}
        {...props}
      >
        {children}
      </select>
    </CrmField>
  );
});

CrmSelect.displayName = 'CrmSelect';

export const CrmSearchInput = forwardRef(({ className = '', ...props }, ref) => (
  <div className={`crm-search-input ${className}`.trim()}>
    <SearchIcon className="crm-search-input__icon" aria-hidden="true" />
    <input ref={ref} type="search" className="crm-control" {...props} />
  </div>
));

CrmSearchInput.displayName = 'CrmSearchInput';

const checkboxDependencyHints = {
  'Enable Current Location': 'Requires browser geolocation permission and an address provider capable of reverse geocoding, such as Google or Mapbox.',
  'Current Location': 'Requires browser geolocation permission and an address provider capable of reverse geocoding, such as Google or Mapbox.',
  'Enable Address Search': 'Requires an address provider such as Google Places or Mapbox search.',
  'Address Search': 'Requires an address provider such as Google Places or Mapbox search.',
  'Enable Map Pin': 'Requires a map-capable address provider and frontend map support.',
  'Map Pin': 'Requires a map-capable address provider and frontend map support.',
  'Online Prepaid': 'Requires an enabled payment gateway profile or valid Razorpay environment credentials.',
  'Cash On Delivery': 'Does not require a payment gateway. Orders are placed as unpaid/manual collection.',
  'Bank Transfer': 'Does not require a payment gateway. Staff must reconcile the transfer manually.',
  'Require Login': 'Depends on an enabled storefront auth profile and available login methods.',
  'Privacy Consent Required': 'Adds a required privacy consent checkbox to checkout.',
  'Terms Required': 'Adds a required terms acceptance checkbox to checkout.',
};

const dependencyHintFor = (label) => checkboxDependencyHints[String(label || '').trim()] || '';

export const CrmCheckbox = forwardRef(({ label, helperText, className = '', ...props }, ref) => {
  const resolvedHelper = helperText || dependencyHintFor(label);
  return (
    <label className={`crm-choice ${className}`.trim()}>
      <input ref={ref} type="checkbox" {...props} />
      <span>{label}{resolvedHelper ? <small className="crm-choice__helper">{resolvedHelper}</small> : null}</span>
    </label>
  );
});

CrmCheckbox.displayName = 'CrmCheckbox';

export const CrmSwitch = forwardRef(({
  label,
  helperText,
  className = '',
  disabled = false,
  ...props
}, ref) => (
  <label className={`crm-switch-row ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
    <span className="crm-switch-row__copy">
      {label ? <span className="crm-switch-row__label">{label}</span> : null}
      {helperText ? <small>{helperText}</small> : null}
    </span>
    <span className="crm-switch">
      <input ref={ref} type="checkbox" role="switch" disabled={disabled} {...props} />
      <span className="crm-switch__track" aria-hidden="true" />
    </span>
  </label>
));

CrmSwitch.displayName = 'CrmSwitch';

export const CrmRadio = forwardRef(({ label, className = '', ...props }, ref) => (
  <label className={`crm-choice ${className}`.trim()}>
    <input ref={ref} type="radio" {...props} />
    <span>{label}</span>
  </label>
));

CrmRadio.displayName = 'CrmRadio';

export default CrmInput;
